import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Shield, TrendingUp, Bell, MapPin, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL 
  ? `${BACKEND_URL}/api.php?request=`
  : `/api.php?request=`;



const HomePage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [topCities, setTopCities] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTopCities();
  }, []);

  const fetchTopCities = async () => {
    try {
      const response = await axios.get(`${API}rankings/global`);
      setTopCities(response.data.slice(0, 6));
    } catch (error) {
      console.error('Error fetching top cities:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    try {
      const response = await axios.get(`${API}search&q=${searchQuery}`);
      if (response.data.length > 0) {
        navigate(`/destination/${response.data[0].destination.id}`);
      } else {
        toast.error('No destination found');
      }
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}subscribe`, {
        email,
        locations: ['global'],
      });
      toast.success('Successfully subscribed to daily alerts!');
      setEmail('');
    } catch (error) {
      toast.error('Subscription failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getScoreClass = (score) => {
    if (score >= 80) return 'score-excellent';
    if (score >= 60) return 'score-good';
    if (score >= 40) return 'score-moderate';
    return 'score-poor';
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="hero-gradient pt-32 pb-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center text-white fade-in">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Know Before You Go
            </h1>
            <p className="text-base sm:text-lg max-w-2xl mx-auto mb-12 opacity-95">
              Real-time women's safety rankings for cities worldwide. Make informed travel decisions with data-driven insights.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto" data-testid="search-form">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search city or country..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 pr-4 py-6 text-base bg-white text-gray-900 rounded-full"
                    data-testid="search-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="px-8 py-6 bg-gray-900 hover:bg-gray-800 text-white rounded-full font-semibold"
                  data-testid="search-button"
                >
                  Search
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Top Safe Cities */}
      <section className="section-padding bg-gray-50" data-testid="top-cities-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">Top Safe Destinations</h2>
            <p className="text-base sm:text-lg text-gray-600">Highly rated cities for women travelers</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {topCities.map((city, index) => {
              if (!city.metrics || !city.destination) return null;
              return (
              <Link
                key={city.destination.id}
                to={`/destination/${city.destination.id}`}
                className="block hover-lift"
                data-testid={`city-card-${index}`}
              >
                <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
                  <div className="relative h-48">
                    <img
                      src={city.destination.image_url}
                      alt={city.destination.city}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 right-4">
                      <div className={`px-4 py-2 rounded-full text-white font-bold ${getScoreClass(city.metrics.composite_wsi)}`}>
                        {city.metrics.composite_wsi?.toFixed(1) || '0.0'}
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-1">{city.destination.city}</h3>
                    <p className="text-sm text-gray-600 mb-3 flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {city.destination.country}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Safety Index</span>
                      <div className="flex items-center gap-1">
                        {city.metrics.trend_7day > 0 ? (
                          <TrendingUp className="w-4 h-4 text-green-500" />
                        ) : city.metrics.trend_7day < 0 ? (
                          <TrendingUp className="w-4 h-4 text-red-500 rotate-180" />
                        ) : null}
                        <span className="text-sm font-semibold">
                          {city.metrics.trend_7day > 0 ? '+' : ''}{city.metrics.trend_7day?.toFixed(1) || '0.0'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            )
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/rankings">
              <Button
                className="px-8 py-6 bg-purple-600 hover:bg-purple-700 text-white rounded-full text-base font-semibold"
                data-testid="view-all-rankings-button"
              >
                View All Rankings <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center fade-in">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-purple-100 flex items-center justify-center">
                <Shield className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold mb-3">Real-time Data</h3>
              <p className="text-sm text-gray-600">
                Updated daily with the latest safety statistics and incident reports from trusted sources.
              </p>
            </div>

            <div className="text-center fade-in">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-teal-100 flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold mb-3">Trend Analysis</h3>
              <p className="text-sm text-gray-600">
                Track safety improvements or concerns with 7-day and 30-day trend indicators.
              </p>
            </div>

            <div className="text-center fade-in">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-purple-100 flex items-center justify-center">
                <Bell className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold mb-3">Daily Alerts</h3>
              <p className="text-sm text-gray-600">
                Get personalized safety updates delivered to your inbox every morning.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="section-padding bg-gradient-to-br from-purple-50 to-teal-50" data-testid="subscribe-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">Stay Informed, Stay Safe</h2>
          <p className="text-base sm:text-lg text-gray-600 mb-8">
            Subscribe to receive daily safety updates for your favorite destinations
          </p>

          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto" data-testid="subscribe-form">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="flex-1 px-6 py-6 text-base rounded-full"
              data-testid="subscribe-email-input"
            />
            <Button
              type="submit"
              disabled={loading}
              className="px-8 py-6 bg-purple-600 hover:bg-purple-700 text-white rounded-full font-semibold"
              data-testid="subscribe-button"
            >
              {loading ? 'Subscribing...' : 'Subscribe'}
            </Button>
          </form>

          <p className="text-xs text-gray-500 mt-4">
            Unsubscribe anytime. We respect your privacy.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;